/* eslint-disable max-nested-callbacks */
import { createSelector } from 'reselect';
import { getVisibleMapDataList, getViewport, getZoom } from './map';
import { getHighlightedOrSelectedElements } from './map-selection';
import { bboxIntersects } from '@utils/geometry-utils';
import {
  createClusters,
  filterClusters,
  createOverlaps,
  mapZoomToClusterPrefix
} from '@utils/cluster-utils';

const getClusterableLayers = createSelector(
  [getVisibleMapDataList],
  (dataList) => {
    if (dataList) {
      return dataList.reduce((filtered, data) => {
        if (data.uiStyle.marker && data.list && data.list.length > 0) {
          filtered[data.id] = data.list;
        }
        return filtered;
      }, {});
    }
    return {};
  }
);

const getOverlaps = createSelector(
  [getClusterableLayers],
  layersList => createOverlaps(layersList)
);

const getPrefixLengths = createSelector(
  [getZoom],
  zoom => mapZoomToClusterPrefix(zoom)
);

const getClusters = createSelector(
  [getOverlaps, getPrefixLengths],
  (overlaps, prefixLength) => createClusters(overlaps, prefixLength)
);


// Memoize per-cluster objects so downstream selectors only recompute when selectionCounts actually change.
const getClustersWithSelections = (() => {
  let prevResult = {};
  return createSelector(
    [getClusters, getHighlightedOrSelectedElements],
    (clusters, layerElements) => {
      const result = {};
      let anyChanged = false;
      Object.entries(clusters).forEach(([geohash, cluster]) => {
        let selectionCount = 0;
        Object.entries(cluster.items).forEach(([layerId, layer]) => {
          if (layerId in layerElements) {
            const elements = layerElements[layerId];
            layer.forEach(itemId => {
              if (elements[itemId]) {
                selectionCount = selectionCount + 1;
              }
            });
          }
        });
        const prev = prevResult[geohash];
        if (prev && prev.cluster === cluster && prev.selectionCount === selectionCount) {
          result[geohash] = prev;
        } else {
          result[geohash] = {cluster, selectionCount};
          anyChanged = true;
        }
      });
      if (!anyChanged && Object.keys(prevResult).length === Object.keys(result).length) {
        return prevResult;
      }
      prevResult = result;
      return result;
    }
  );
})();

export const filterClustersBbox = (clusters, bbox) => (
  filterClusters(clusters, cluster => bboxIntersects(cluster.bbox, bbox))
);

export const getVisibleClusters = createSelector(
  [getClustersWithSelections, getViewport],
  (selectionCluters, { bbox }) => {
    if (!bbox) {
      return {};
    }
    return filterClusters(selectionCluters, ({cluster}) => bboxIntersects(cluster.bbox, bbox));
  }
);

// Reuse {mapElement, clusterSelection} item objects when neither the element nor its cluster selection changed.
// This keeps MapFigure PureComponent props stable, avoiding unnecessary re-renders.
const getClusteredData = (() => {
  const prevItemMap = new WeakMap();
  return createSelector(
    [getClustersWithSelections, getVisibleMapDataList, getPrefixLengths],
    (clusterSelections, dataList, prefixLength) => {
      const annotatedData = {};
      dataList.forEach(layerData => {
        const annotatedList = [];
        layerData.list.forEach(element => {
          const clusterPrefix = element.geohash && element.geohash.substring(0, prefixLength);
          const cs = clusterPrefix && clusterSelections[clusterPrefix] || null;
          const prev = prevItemMap.get(element);
          if (prev && prev.clusterSelection === cs) {
            annotatedList.push(prev);
          } else {
            const item = {mapElement: element, clusterSelection: cs};
            prevItemMap.set(element, item);
            annotatedList.push(item);
          }
        });
        annotatedData[layerData.id] = {...layerData, list: annotatedList};
      });
      return annotatedData;
    }
  );
})();

export const getViewportData = createSelector(
  [getClusteredData, getViewport],
  (data, {bbox}) => {
    const filteredData = {};
    let filterFunction = () => false;
    if (bbox && bbox.length === 4) {
      filterFunction = ({mapElement}) => bboxIntersects(mapElement.bbox, bbox);
    }
    if (data) {
      Object.entries(data).forEach(([layerId, layerData]) => {
        filteredData[layerId] = {
          ...layerData,
          list: layerData.list.filter(filterFunction)
        };
      });
    }
    return filteredData;
  }
);
